import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useTheme } from "../../contexts/ThemeContext";
import { API_BASE_URL } from "../../config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion } from "framer-motion";
import Layout from "../Layout/Layout";
import {
  Search,
  Play,
  Square,
  Trash2,
  Send,
  MessageCircle,
  Users,
  CheckCircle,
  Clock,
  Loader2,
} from "lucide-react";
import Loader from "../utils/Loader";
import TrimText from "../utils/TrimText";
import AppButton from "../shared/AppButton.jsx";
import GlobalDataTable from "../shared/GlobalDataTable.jsx";

const SentMessages = () => {
  const { theme } = useTheme();
  const [sentMessages, setSentMessages] = useState([]);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const sendIntervalRef = useRef(null);
  const isSendingRef = useRef(false);
  const [autoSendActive, setAutoSendActive] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetchSentMessages = async () => {
      const token = window.__appSession.getToken();

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/whatsapp/sent-messages`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setSentMessages(response.data.data);
      } catch (error) {
        console.error("Error fetching sent messages:", error);
        toast.error("Failed to fetch sent messages.");
      } finally {
        setLoading(false);
      }
    };

    fetchSentMessages();
  }, []);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredMessages = sentMessages.filter((msg) => {
    const search = searchQuery.toLowerCase();
    return (
      (msg.mobileNo && msg.mobileNo.toLowerCase().includes(search)) ||
      (msg.name && msg.name.toLowerCase().includes(search)) ||
      (msg.message && msg.message.toLowerCase().includes(search)) ||
      (msg.filePathUrl && msg.filePathUrl.toLowerCase().includes(search)) ||
      (msg.status && msg.status.toLowerCase().includes(search)) ||
      (msg._id && msg._id.toLowerCase().includes(search))
    );
  });

  const handleSendMessage = async (
    messageId,
    mobileNo,
    personalizedMessage,
    fileUrls = [],
  ) => {
    const token = window.__appSession.getToken();

    try {
      await axios.post(
        `${API_BASE_URL}/api/whatsapp/send-message`,
        {
          receiverMobileNo: mobileNo,
          message: [personalizedMessage],
          filePathUrl: fileUrls,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      await axios.post(
        `${API_BASE_URL}/api/whatsapp/update-message-status2`,
        { messageId, status: "sent" },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      toast.success(`Message sent to ${mobileNo}!`);
    } catch (error) {
      console.error("Error:", error);
      toast.error(`Failed to send message to ${mobileNo}.`);
    }
  };

  const handleResendMessage = async (
    messageId,
    mobileNo,
    name,
    message,
    fileUrls,
  ) => {
    await handleSendMessage(messageId, mobileNo, message, fileUrls);
    setSentMessages((prevMessages) =>
      prevMessages.map((msg) =>
        msg._id === messageId ? { ...msg, status: "sent" } : msg,
      ),
    );
  };

  const startAutoSend = () => {
    setAutoSendActive(true);
    isSendingRef.current = true;

    const sendMessages = async () => {
      const pendingMessages = sentMessages.filter(
        (msg) => msg.status === "pending",
      );

      if (pendingMessages.length === 0) {
        console.log("No pending messages to send.");
        stopAutoSend();
        return;
      }

      for (let i = 0; i < pendingMessages.length; i++) {
        if (!isSendingRef.current) {
          console.log("Sending stopped.");
          break;
        }

        const msg = pendingMessages[i];
        await handleSendMessage(
          msg._id,
          msg.mobileNo,
          msg.message,
          msg.filePathUrl,
        );
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      console.log("All pending messages sent!");
      stopAutoSend();
    };

    sendMessages();
  };

  const stopAutoSend = () => {
    setAutoSendActive(false);
    isSendingRef.current = false;
    if (sendIntervalRef.current) {
      clearInterval(sendIntervalRef.current);
    }
    window.location.reload();
  };

  const toggleSelectMessage = (messageId) => {
    setSelectedMessages((prevSelected) => {
      if (prevSelected.includes(messageId)) {
        return prevSelected.filter((id) => id !== messageId);
      } else {
        return [...prevSelected, messageId];
      }
    });
  };

  const handleDeleteMessages = async () => {
    const token = window.__appSession.getToken();
    const messagesToDelete = selectedMessages;

    try {
      await axios.post(
        `${API_BASE_URL}/api/whatsapp/delete-messages`,
        { messageIds: messagesToDelete },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setSentMessages((prevMessages) =>
        prevMessages.filter((msg) => !messagesToDelete.includes(msg._id)),
      );
      setSelectedMessages([]);
      toast.success("Messages deleted successfully!");
    } catch (error) {
      console.error("Error deleting messages:", error);
      toast.error("Failed to delete messages.");
    }
  };

  const pendingCount = sentMessages.filter(
    (msg) => msg.status === "pending",
  ).length;
  const sentCount = sentMessages.filter((msg) => msg.status === "sent").length;

  return (
    <Layout>
      <div className="space-y-3 transition-colors duration-300">
        {/* Loading Overlay */}
        {loading && <Loader theme={theme} text="Loading messages..." />}

        <div>
          <div className="grid grid-cols-1 md:grid-cols-[max-content_1fr] gap-3 mb-3">
            {/* Header */}
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className={`glass-surface-strong ${theme.contentBg} rounded-2xl sm:w-fit border ${theme.contentBorder} px-3 py-2`}
            >
              <div className="flex sm:w-fit items-center gap-3">
                <div className="rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 p-2">
                  <MessageCircle size={20} className="text-white" />
                </div>
                <h1 className="text-lg font-bold text-slate-900">
                  Sent Messages
                </h1>
              </div>

              {/* Stats */}
              {/* <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div
                className={`${theme.sidebarBg} p-3 rounded-lg border ${theme.sidebarBorder}`}
              >
                <div className="flex items-center gap-2">
                  <Users size={16} className={theme.iconColor} />
                  <span className={`text-xs ${theme.sidebarText} opacity-70`}>
                    Total
                  </span>
                </div>
                <p className={`text-lg font-semibold ${theme.sidebarText}`}>
                  {filteredMessages.length}
                </p>
              </div>

              <div
                className={`${theme.sidebarBg} p-3 rounded-lg border ${theme.sidebarBorder}`}
              >
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-500" />
                  <span className={`text-xs ${theme.sidebarText} opacity-70`}>
                    Sent
                  </span>
                </div>
                <p className={`text-lg font-semibold ${theme.sidebarText}`}>
                  {sentCount}
                </p>
              </div>

              <div
                className={`${theme.sidebarBg} p-3 rounded-lg border ${theme.sidebarBorder}`}
              >
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-yellow-500" />
                  <span className={`text-xs ${theme.sidebarText} opacity-70`}>
                    Pending
                  </span>
                </div>
                <p className={`text-lg font-semibold ${theme.sidebarText}`}>
                  {pendingCount}
                </p>
              </div>
            </div> */}
            </motion.div>

            {/* Controls */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className={`glass-surface ${theme.contentBg} rounded-2xl border ${theme.contentBorder} p-2`}
            >
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_max-content] gap-4">
                {/* Search */}
                <div className="relative flex-1">
                  <Search
                    size={16}
                    className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.iconColor}`}
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search messages..."
                    className={`w-full rounded-xl border border-indigo-200 bg-white/85 pl-10 pr-3 py-2 text-sm text-slate-700 transition-all focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <AppButton
                    onClick={autoSendActive ? stopAutoSend : startAutoSend}
                    size="sm"
                    variant={autoSendActive ? "danger" : "submit"}
                    className="gap-2"
                  >
                    {autoSendActive ? (
                      <>
                        <Square size={14} />
                        Stop Auto Send
                      </>
                    ) : (
                      <>
                        <Play size={14} />
                        Start Auto Send
                      </>
                    )}
                  </AppButton>

                  <AppButton
                    onClick={handleDeleteMessages}
                    disabled={selectedMessages.length === 0}
                    size="sm"
                    variant="danger"
                    className="gap-2"
                  >
                    <Trash2 size={14} />
                    Delete Selected ({selectedMessages.length})
                  </AppButton>
                </div>
              </div>
            </motion.div>
          </div>
          {/* Messages Table */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className={`glass-surface ${theme.contentBg} rounded-2xl border ${theme.contentBorder} overflow-hidden`}
          >
            <GlobalDataTable
              ariaLabel="Sent messages table"
              columns={[
                {
                  key: "select",
                  header: (
                    <input
                      type="checkbox"
                      checked={
                        selectedMessages.length === filteredMessages.length &&
                        filteredMessages.length > 0
                      }
                      onChange={() => {
                        if (selectedMessages.length === filteredMessages.length) {
                          setSelectedMessages([]);
                        } else {
                          setSelectedMessages(filteredMessages.map((msg) => msg._id));
                        }
                      }}
                      className={`rounded border-gray-300 ${theme.inputFocusRing}`}
                      aria-label="Select all messages"
                    />
                  ),
                  render: (msg) => (
                    <input
                      type="checkbox"
                      checked={selectedMessages.includes(msg._id)}
                      onChange={() => toggleSelectMessage(msg._id)}
                      className={`rounded border-gray-300 ${theme.inputFocusRing}`}
                      aria-label={`Select message ${msg._id}`}
                    />
                  ),
                },
                {
                  key: "mobileNo",
                  header: "Mobile No",
                  cellClassName: `text-sm ${theme.sidebarText} font-mono`,
                  render: (msg) => msg.mobileNo,
                },
                {
                  key: "name",
                  header: "Name",
                  cellClassName: `text-sm ${theme.sidebarText}`,
                  render: (msg) => msg.name,
                },
                {
                  key: "message",
                  header: "Message",
                  cellClassName: `text-sm ${theme.sidebarText}`,
                  render: (msg) => (
                    <div className="max-w-xs" title={msg.message}>
                      <TrimText text={msg.message} maxLen={60} />
                    </div>
                  ),
                },
                {
                  key: "status",
                  header: "Status",
                  render: (msg) => (
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        msg.status === "sent"
                          ? "border border-emerald-200 bg-emerald-100 text-emerald-700"
                          : "border border-amber-200 bg-amber-100 text-amber-700"
                      }`}
                    >
                      {msg.status === "sent" ? (
                        <CheckCircle size={12} className="mr-1" />
                      ) : (
                        <Clock size={12} className="mr-1" />
                      )}
                      {msg.status}
                    </span>
                  ),
                },
                {
                  key: "action",
                  header: "Action",
                  render: (msg) =>
                    msg.status === "pending" ? (
                      <AppButton
                        size="sm"
                        variant="submit"
                        className="gap-1"
                        onClick={() =>
                          handleResendMessage(
                            msg._id,
                            msg.mobileNo,
                            msg.name,
                            msg.message,
                            msg.filePathUrl,
                          )
                        }
                      >
                        <Send size={12} />
                        Send
                      </AppButton>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    ),
                },
              ]}
              rows={filteredMessages}
              rowKey={(msg) => msg._id}
              page={0}
              rowsPerPage={Math.max(filteredMessages.length || 1, 1)}
              totalCount={filteredMessages.length}
              onPageChange={() => {}}
              onRowsPerPageChange={() => {}}
              loading={loading}
              showPagination={false}
              emptyText="No messages found"
            />
          </motion.div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </Layout>
  );
};

export default SentMessages;

// import React, { useEffect, useState, useRef } from "react";
// import axios from "axios";
// import { API_BASE_URL } from "../../config";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import { motion } from "framer-motion"; // For animations
// import Layout from "../Layout/Layout"; // Import the enhanced Layout component

// const Footer = () => {
//   return (
//     <footer className="bg-gradient-to-r from-green-600 to-teal-500 text-white py-4 text-center shadow-inner">
//       <p className="text-sm tracking-wide">
//         Â© {new Date().getFullYear()} Smart ItBox. All rights reserved.
//       </p>
//     </footer>
//   );
// };

// const SentMessages = () => {
//   const [sentMessages, setSentMessages] = useState([]);
//   const [selectedMessages, setSelectedMessages] = useState([]);
//   const [searchQuery, setSearchQuery] = useState("");
//   const sendIntervalRef = useRef(null); // To keep track of the interval
//   const isSendingRef = useRef(false); // Ref to track whether sending is active
//   const [autoSendActive, setAutoSendActive] = useState(false);
//   const [loading, setLoading] = useState(false);

//   useEffect(() => {
//     setLoading(true);
//     const fetchSentMessages = async () => {
//       const token = window.__appSession.getToken();

//       try {
//         const response = await axios.get(
//           `${API_BASE_URL}/api/whatsapp/sent-messages`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//             },
//           }
//         );
//         setSentMessages(response.data.data);
//       } catch (error) {
//         console.error("Error fetching sent messages:", error);
//         toast.error("Failed to fetch sent messages.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchSentMessages();
//   }, []);

//   // Function to handle search by mobile number
//   const handleSearchChange = (event) => {
//     setSearchQuery(event.target.value);
//   };

//   // Filter the messages based on search query (by mobile number)
//   const filteredMessages = sentMessages.filter(
//     (msg) => msg.mobileNo && msg.mobileNo.includes(searchQuery)
//   );

//   const handleSendMessage = async (
//     messageId,
//     mobileNo,
//     personalizedMessage,
//     fileUrls = []
//   ) => {
//     const token = window.__appSession.getToken();

//     try {
//       await axios.post(
//         `${API_BASE_URL}/api/whatsapp/send-message`,
//         {
//           receiverMobileNo: mobileNo,
//           message: [personalizedMessage],
//           filePathUrl: fileUrls,
//         },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       // Update the message status in the database
//       await axios.post(
//         `${API_BASE_URL}/api/whatsapp/update-message-status2`,
//         { messageId, status: "sent" },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       toast.success(`Message sent to ${mobileNo}!`);
//     } catch (error) {
//       console.error("Error:", error);
//       toast.error(`Failed to send message to ${mobileNo}.`);
//     }
//   };

//   const handleResendMessage = async (
//     messageId,
//     mobileNo,
//     name,
//     message,
//     fileUrls
//   ) => {
//     await handleSendMessage(messageId, mobileNo, message, fileUrls);
//     setSentMessages((prevMessages) =>
//       prevMessages.map((msg) =>
//         msg._id === messageId ? { ...msg, status: "sent" } : msg
//       )
//     );
//   };

//   const handleSendToAllPending = async () => {
//     const token = window.__appSession.getToken();
//     const pendingMessages = sentMessages.filter(
//       (msg) => msg.status === "pending"
//     );

//     if (pendingMessages.length === 0) {
//       toast.info("No pending messages to send.");
//       return;
//     }

//     for (const msg of pendingMessages) {
//       await handleSendMessage(
//         msg._id,
//         msg.mobileNo,
//         msg.message,
//         msg.filePathUrl
//       );
//     }
//   };

//   const startAutoSend = () => {
//     setAutoSendActive(true);
//     isSendingRef.current = true;

//     const sendMessages = async () => {
//       const pendingMessages = sentMessages.filter(
//         (msg) => msg.status === "pending"
//       );

//       if (pendingMessages.length === 0) {
//         console.log("No pending messages to send.");
//         stopAutoSend();
//         return;
//       }

//       for (let i = 0; i < pendingMessages.length; i++) {
//         if (!isSendingRef.current) {
//           console.log("Sending stopped.");
//           break;
//         }

//         const msg = pendingMessages[i];
//         await handleSendMessage(
//           msg._id,
//           msg.mobileNo,
//           msg.message,
//           msg.filePathUrl
//         );
//         await new Promise((resolve) => setTimeout(resolve, 2000));
//       }

//       console.log("All pending messages sent!");
//       stopAutoSend();
//     };

//     sendMessages();
//   };

//   const stopAutoSend = () => {
//     setAutoSendActive(false);
//     isSendingRef.current = false;
//     if (sendIntervalRef.current) {
//       clearInterval(sendIntervalRef.current);
//     }
//     window.location.reload();
//   };

//   const toggleSelectMessage = (messageId) => {
//     setSelectedMessages((prevSelected) => {
//       if (prevSelected.includes(messageId)) {
//         return prevSelected.filter((id) => id !== messageId);
//       } else {
//         return [...prevSelected, messageId];
//       }
//     });
//   };

//   const handleDeleteMessages = async () => {
//     const token = window.__appSession.getToken();
//     const messagesToDelete = selectedMessages;

//     try {
//       await axios.post(
//         `${API_BASE_URL}/api/whatsapp/delete-messages`,
//         { messageIds: messagesToDelete },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       setSentMessages((prevMessages) =>
//         prevMessages.filter((msg) => !messagesToDelete.includes(msg._id))
//       );
//       setSelectedMessages([]);
//       toast.success("Messages deleted successfully!");
//     } catch (error) {
//       console.error("Error deleting messages:", error);
//       toast.error("Failed to delete messages.");
//     }
//   };

//   return (
//     <Layout>
//       <motion.main
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         transition={{ duration: 0.8 }}
//         className="flex-grow w-full min-h-[calc(100vh-4rem)] py-6 px-4 sm:px-6"
//       >
//         {loading && (
//           <motion.div
//             className="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50"
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//           >
//             <svg
//               className="animate-spin h-12 w-12 text-[#25D366]"
//               xmlns="http://www.w3.org/2000/svg"
//               viewBox="0 0 24 24"
//             >
//               <circle
//                 className="opacity-25"
//                 cx="12"
//                 cy="12"
//                 r="10"
//                 stroke="currentColor"
//                 strokeWidth="4"
//               />
//               <path
//                 className="opacity-75"
//                 fill="currentColor"
//                 d="M4 12a8 8 0 1 1 16 0A8 8 0 0 1 4 12z"
//               />
//             </svg>
//           </motion.div>
//         )}

//         <div className="bg-white/90 backdrop-blur-md shadow-2xl rounded-2xl p-6 sm:p-8 w-full max-w-6xl mx-auto border border-gray-200 transition-all">
//           <h1 className="text-3xl sm:text-4xl font-bold text-center mb-6 text-[#25D366] tracking-tight font-serif">
//             Sent Messages
//           </h1>

//           {/* Search Input & Buttons */}
//           <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
//             <motion.div
//               whileFocus={{ scale: 1.02 }}
//               className="relative w-full sm:w-64"
//             >
//               <input
//                 type="text"
//                 value={searchQuery}
//                 onChange={handleSearchChange}
//                 placeholder="Search by mobile number..."
//                 className="w-full p-3 text-sm bg-gray-50/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] transition-all"
//               />
//               <svg
//                 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500"
//                 xmlns="http://www.w3.org/2000/svg"
//                 fill="none"
//                 viewBox="0 0 24 24"
//                 stroke="currentColor"
//               >
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth="2"
//                   d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
//                 />
//               </svg>
//             </motion.div>

//             <div className="flex flex-col sm:flex-row gap-3">
//               <motion.button
//                 whileHover={{
//                   scale: 1.05,
//                   boxShadow: "0 8px 25px rgba(34, 197, 94, 0.3)",
//                 }}
//                 whileTap={{ scale: 0.95 }}
//                 onClick={autoSendActive ? stopAutoSend : startAutoSend}
//                 className={`${
//                   autoSendActive
//                     ? "bg-red-500 hover:bg-red-600"
//                     : "bg-green-500 hover:bg-green-600"
//                 } text-white py-2 px-4 rounded-lg shadow-md transition-all`}
//               >
//                 {autoSendActive ? "Stop Auto Send" : "Start Auto Send"}
//               </motion.button>

//               <motion.button
//                 whileHover={{
//                   scale: 1.05,
//                   boxShadow: "0 8px 25px rgba(239, 68, 68, 0.3)",
//                 }}
//                 whileTap={{ scale: 0.95 }}
//                 onClick={handleDeleteMessages}
//                 disabled={selectedMessages.length === 0}
//                 className={`bg-red-500 text-white py-2 px-4 rounded-lg shadow-md transition-all ${
//                   selectedMessages.length === 0
//                     ? "opacity-50 cursor-not-allowed"
//                     : "hover:bg-red-600"
//                 }`}
//               >
//                 Delete Selected
//               </motion.button>
//             </div>
//           </div>

//           {/* Total Messages Count */}
//           <h2 className="text-xl sm:text-2xl font-semibold mb-4 text-gray-800 tracking-wide">
//             Total Messages: {filteredMessages.length}
//           </h2>

//           {/* Table */}
//           <div className="overflow-x-auto">
//             <table className="min-w-full border border-gray-200 rounded-lg shadow-sm">
//               <thead>
//                 <tr className="bg-[#25D366] text-white">
//                   <th className="border border-gray-200 p-3 text-left">
//                     Mobile No
//                   </th>
//                   <th className="border border-gray-200 p-3 text-left">Name</th>
//                   <th className="border border-gray-200 p-3 text-left">
//                     Message
//                   </th>
//                   <th className="border border-gray-200 p-3 text-left">
//                     Status
//                   </th>
//                   <th className="border border-gray-200 p-3 text-left">
//                     Action
//                   </th>
//                   <th className="border border-gray-200 p-3 w-12">
//                     <input
//                       type="checkbox"
//                       checked={
//                         selectedMessages.length === filteredMessages.length
//                       }
//                       onChange={() => {
//                         if (
//                           selectedMessages.length === filteredMessages.length
//                         ) {
//                           setSelectedMessages([]);
//                         } else {
//                           setSelectedMessages(
//                             filteredMessages.map((msg) => msg._id)
//                           );
//                         }
//                       }}
//                     />
//                   </th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {filteredMessages.length === 0 ? (
//                   <tr>
//                     <td colSpan={6} className="text-center p-4 text-gray-600">
//                       No messages found.
//                     </td>
//                   </tr>
//                 ) : (
//                   filteredMessages.map((msg, index) => (
//                     <motion.tr
//                       key={index}
//                       initial={{ opacity: 0, y: 10 }}
//                       animate={{ opacity: 1, y: 0 }}
//                       transition={{ duration: 0.3 }}
//                       className="hover:bg-gray-50 transition-all"
//                     >
//                       <td className="border border-gray-200 p-3">
//                         {msg.mobileNo}
//                       </td>
//                       <td className="border border-gray-200 p-3">{msg.name}</td>
//                       <td className="border border-gray-200 p-3">
//                         {msg.message}
//                       </td>
//                       <td className="border border-gray-200 p-3">
//                         <span
//                           className={`inline-block px-2 py-1 rounded-full text-sm ${
//                             msg.status === "sent"
//                               ? "bg-green-200 text-green-800"
//                               : "bg-yellow-200 text-yellow-800"
//                           }`}
//                         >
//                           {msg.status}
//                         </span>
//                       </td>
//                       <td className="border border-gray-200 p-3">
//                         {msg.status === "pending" && (
//                           <motion.button
//                             whileHover={{ scale: 1.05 }}
//                             whileTap={{ scale: 0.95 }}
//                             onClick={() =>
//                               handleResendMessage(
//                                 msg._id,
//                                 msg.mobileNo,
//                                 msg.name,
//                                 msg.message,
//                                 msg.filePathUrl
//                               )
//                             }
//                             className="bg-primary-500 text-white py-1 px-3 rounded-lg shadow-md hover:bg-primary-600 transition-all"
//                           >
//                             Send
//                           </motion.button>
//                         )}
//                       </td>
//                       <td className="border border-gray-200 p-3 w-12 text-center">
//                         <input
//                           type="checkbox"
//                           checked={selectedMessages.includes(msg._id)}
//                           onChange={() => toggleSelectMessage(msg._id)}
//                         />
//                       </td>
//                     </motion.tr>
//                   ))
//                 )}
//               </tbody>
//             </table>
//           </div>

//           <ToastContainer />
//         </div>
//       </motion.main>
//     </Layout>
//   );
// };

// export default SentMessages;
